import { ref } from 'vue'
import { markAsRead, markAllAsRead } from '@/api/modules/notification'
import type { useNotificationList } from './useNotificationList'
import type { useNotificationUnread } from './useNotificationUnread'

type ListState = ReturnType<typeof useNotificationList>
type UnreadState = ReturnType<typeof useNotificationUnread>

/** 协调已读写入；scope 隔离身份，list 为当前页，unread 为独立统计状态。 */
class NotificationReadState {
  readonly readPending = ref(false)
  readonly readAllPending = ref(false)
  readonly actionError = ref('')
  private generation = 0
  /** 保存状态依赖；不触发接口请求。 */
  constructor(
    private readonly scope: () => string,
    private readonly list: ListState,
    private readonly unread: UnreadState,
  ) {}

  /** 身份切换时作废写操作的本地副作用，服务器请求本身仍由原身份处理。 */
  reset = () => {
    this.generation++
    this.readPending.value = false
    this.readAllPending.value = false
    this.actionError.value = ''
  }

  /** 标记 id 对应当前页通知；返回本次是否成功从未读改为已读。 */
  readOne = async (id: number): Promise<boolean> => {
    const target = this.list.notifications.value.find((item) => item.id === id)
    if (!this.scope() || id <= 0 || !target || target.isRead || this.readPending.value) return false
    const identity = this.scope()
    const version = this.generation
    const snapshot = this.unread.beginMutation()
    this.readPending.value = true
    this.actionError.value = ''
    target.isRead = true
    this.unread.applyRead(target)
    let success = false
    try {
      await markAsRead(id)
      success = true
    } catch {
      if (identity === this.scope() && version === this.generation) {
        target.isRead = false
        this.actionError.value = '标记已读失败，消息仍保留未读状态'
      }
    } finally {
      if (identity === this.scope() && version === this.generation) {
        // 不等待统计请求才释放写锁；版本隔离防止它覆盖下一次写操作。
        this.readPending.value = false
        void this.unread.finishMutation(success ? undefined : snapshot)
      }
    }
    return success && identity === this.scope() && version === this.generation
  }

  /** 标记账号的全部分类/分页为已读；失败恢复快照，成功重查当前页与统计。 */
  readAll = async (): Promise<boolean> => {
    if (!this.scope() || this.readPending.value || this.unread.unreadCount.value <= 0) return false
    const identity = this.scope()
    const version = this.generation
    const snapshot = this.unread.beginMutation()
    this.list.invalidate()
    const previous = this.list.notifications.value.map((item) => ({ item, isRead: item.isRead }))
    this.readPending.value = true
    this.readAllPending.value = true
    this.actionError.value = ''
    this.list.notifications.value.forEach((item) => {
      item.isRead = true
    })
    this.unread.applyRead()
    let success = false
    try {
      await markAllAsRead()
      success = true
    } catch {
      if (identity === this.scope() && version === this.generation) {
        previous.forEach(({ item, isRead }) => {
          item.isRead = isRead
        })
        this.actionError.value = '全部标为已读失败，请重试'
      }
    } finally {
      if (identity === this.scope() && version === this.generation) {
        await this.unread.finishMutation(success ? undefined : snapshot)
        if (success && identity === this.scope() && version === this.generation)
          await this.list.fetchNotifications()
        if (identity === this.scope() && version === this.generation) {
          this.readPending.value = false
          this.readAllPending.value = false
        }
      }
    }
    return success && identity === this.scope() && version === this.generation
  }
}

/** 创建隔离的通知状态；参数分别提供当前身份及相关数据状态。 */
export function useNotificationReadActions(
  scope: () => string,
  list: ListState,
  unread: UnreadState,
) {
  const state = new NotificationReadState(scope, list, unread)
  return {
    readPending: state.readPending,
    readAllPending: state.readAllPending,
    actionError: state.actionError,
    readOne: state.readOne,
    readAll: state.readAll,
    reset: state.reset,
  }
}
